// src/features/previsionf/PrevisionSousFamilleView.tsx  — version v6
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  getForecastDepots,
  getForecastHistorique,
  getForecastToutesSousFamilles,
  tryPrevisionHorizon,
} from "../api";
import { session } from "../../../shared/api/session";
import { Spinner } from "../../../shared/ui/Spinner";
import { colors } from "../../../shared/ui/tokens";

// ── Types locaux ──────────────────────────────────────────────────────────────
interface FamilleParentDto {
  codFamp: string;
  designation: string;
}
interface SousFamDto {
  codFam: string;
  desgFam: string;
  codFamp: string;
}
interface DepotRecord {
  code_dep: string;
  ca_ventes: number;
  ca_retours: number;
  ca_net: number;
  nb_lignes: number;
  taux_retour: number;
}

// ── Type complet de la réponse mappée (indépendant de PrevisionFResponse) ────
interface ExtPrevisionFResponse {
  success: boolean;
  famille?: string;
  sous_famille?: string;
  depot?: string;
  horizon?: number;
  ca_total?: number | null;
  date_from?: string;
  date_to?: string;
  previsions?: Array<{
    step: number;
    date: string;
    ca_predit: number;
    borne_basse: number;
    borne_haute: number;
  }>;
  // Champs construits côté frontend depuis l'historique réel
  forecastCa: number | null;
  sameMonthHistory: Array<{ year: number; month: number; caTotal: number }>;
  weeklyHistory: Array<{ week: string; caTotal: number; nbJours: number }>;
  [key: string]: any;
}

type RowStatus = "idle" | "loading" | "loaded" | "error" | "nodata";

interface RowState {
  status: RowStatus;
  data?: ExtPrevisionFResponse;
  error?: string;
  forecastYear?: number;
  forecastMonth?: number;
  depot?: string | null;
  dateFrom?: string;
  dateTo?: string;
}

// ── Palette ───────────────────────────────────────────────────────────────────
const NW = {
  bg: colors.neutral[100],
  card: colors.neutral[0],
  violet: colors.brand[600],
  violetDark: colors.brand[700],
  violetMed: colors.accent[300],
  violetLight: colors.brand[100],
  violetBorder: "rgba(29,91,168,0.16)",
  text: colors.neutral[900],
  textMuted: colors.brand[500],
  green: colors.accent[500],
  greenBg: colors.neutral[50],
  red: colors.danger[500],
  redBg: colors.neutral[100],
  blue: colors.brand[500],
  blueBg: colors.brand[50],
  orange: colors.warning[600],
  orangeBg: colors.neutral[50],
  border: "rgba(29,91,168,0.13)",
  shadow: "0 2px 20px rgba(29,91,168,0.09)",
  shadowStrong: "0 4px 28px rgba(29,91,168,0.15)",
};

const MONTHS_FR = [
  "",
  "Janvier",
  "Février",
  "Mars",
  "Avril",
  "Mai",
  "Juin",
  "Juillet",
  "Août",
  "Septembre",
  "Octobre",
  "Novembre",
  "Décembre",
];

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtCa = (v: number | undefined | null): string => {
  if (v == null || isNaN(v as number)) return "—";
  return (
    new Intl.NumberFormat("fr-TN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(v) + " DT"
  );
};

const fmtPct = (v: number) =>
  (v > 0 ? "▲ +" : v < 0 ? "▼ " : "") + v.toFixed(1) + "%";

function fmtDateFr(s: string): string {
  if (!s) return "";
  const [y, m, d] = s.split("-");
  return `${d}/${m}/${y}`;
}

function parseDateFromStr(s: string): { year: number; month: number } | null {
  if (!s) return null;
  const d = new Date(s);
  if (isNaN(d.getTime())) return null;
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

function getWeekBounds(
  dateFrom: string,
  weekOffset: number,
): { start: Date; end: Date } {
  const ref = new Date(dateFrom);
  const dayOfWeek = (ref.getDay() + 6) % 7;
  const s1End = new Date(ref);
  s1End.setDate(s1End.getDate() - dayOfWeek - 1);
  const s1Start = new Date(s1End);
  s1Start.setDate(s1Start.getDate() - 6);
  if (weekOffset === 1) {
    return { start: s1Start, end: s1End };
  } else {
    const s2End = new Date(s1Start);
    s2End.setDate(s2End.getDate() - 1);
    const s2Start = new Date(s2End);
    s2Start.setDate(s2Start.getDate() - 6);
    return { start: s2Start, end: s2End };
  }
}

function toYMD(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function fmtShortDate(d: Date): string {
  return `${d.getDate()}/${d.getMonth() + 1}`;
}

function getMonthBounds(
  dateFrom: string,
  monthOffset: number,
): { label: string; from: string; to: string } {
  const ref = new Date(dateFrom);
  const y = ref.getFullYear();
  const m = ref.getMonth() + 1;
  const targetM = m - monthOffset;
  let targetYear = y;
  let targetMonth = targetM;
  if (targetMonth <= 0) {
    targetMonth += 12;
    targetYear -= 1;
  }
  const firstDay = new Date(targetYear, targetMonth - 1, 1);
  const lastDay = new Date(targetYear, targetMonth, 0);
  return {
    label: `${MONTHS_FR[targetMonth]} ${targetYear}`,
    from: toYMD(firstDay),
    to: toYMD(lastDay),
  };
}

async function fetchDepots(): Promise<DepotRecord[]> {
  try {
    const r = await getForecastDepots<any>();
    return (r.data ?? []).map((d: any) => ({
      ...d,
      // Le backend /depots retourne { codDep, libelle } — on normalise vers code_dep
      code_dep: d.code_dep ?? d.codDep ?? d.cod_dep ?? "",
      ca_ventes: 0,
      ca_retours: 0,
      ca_net: 0,
      nb_lignes: 0,
      taux_retour: 0,
    }));
  } catch {
    return [];
  }
}

// ── DataCell ──────────────────────────────────────────────────────────────────
const DataCell: React.FC<{
  value: number | undefined | null;
  highlight?: boolean;
  muted?: boolean;
  compareWith?: number | null;
  incomplete?: boolean;
}> = ({ value, highlight, muted, compareWith, incomplete }) => {
  const hasCompare = compareWith != null && compareWith > 0 && value != null;
  const pct = hasCompare
    ? ((value! - compareWith!) / compareWith!) * 100
    : null;
  const pctColor =
    pct == null
      ? NW.textMuted
      : pct > 0
        ? NW.green
        : pct < 0
          ? NW.red
          : NW.textMuted;

  return (
    <td
      style={{
        padding: "0 14px",
        textAlign: "right",
        verticalAlign: "middle",
        background: highlight ? "rgba(29,91,168,0.045)" : "transparent",
        borderLeft: highlight ? `3px solid ${NW.violet}22` : "none",
      }}
    >
      <div
        style={{
          fontWeight: highlight ? 800 : muted ? 600 : 700,
          fontSize: highlight ? 14 : 13,
          color: highlight
            ? NW.violet
            : muted
              ? NW.textMuted
              : value == null
                ? NW.border
                : NW.text,
          lineHeight: 1.2,
        }}
      >
        {fmtCa(value)}
      </div>
      {pct != null && (
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: pctColor,
            marginTop: 2,
          }}
        >
          {fmtPct(pct)}
        </div>
      )}
      {incomplete && (
        <div
          style={{
            fontSize: 9,
            color: NW.orange,
            fontWeight: 700,
            marginTop: 2,
          }}
        >
          ⚠ sem. en cours
        </div>
      )}
    </td>
  );
};

// ── ImageModal ────────────────────────────────────────────────────────────────
const ImageModal: React.FC<{
  imageUrl: string;
  title: string;
  onClose: () => void;
}> = ({ imageUrl, title, onClose }) => (
  <div
    onClick={onClose}
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(15,23,42,0.72)",
      zIndex: 9999,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 24,
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        background: NW.card,
        borderRadius: 20,
        padding: 24,
        maxWidth: "92vw",
        maxHeight: "92vh",
        overflow: "auto",
        boxShadow: "0 20px 60px rgba(15,23,42,0.5)",
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span
          style={{
            fontFamily: "Poppins, Inter, system-ui, sans-serif",
            fontWeight: 800,
            fontSize: 15,
            color: NW.violet,
          }}
        >
          📊 {title}
        </span>
        <button
          onClick={onClose}
          style={{
            background: NW.redBg,
            border: "none",
            borderRadius: 8,
            color: NW.red,
            fontWeight: 800,
            fontSize: 14,
            cursor: "pointer",
            padding: "4px 10px",
          }}
        >
          ✕ Fermer
        </button>
      </div>
      <img
        src={imageUrl}
        alt={title}
        style={{ maxWidth: "100%", borderRadius: 10, display: "block" }}
        onError={(e) => {
          (e.target as HTMLImageElement).style.display = "none";
        }}
      />
    </div>
  </div>
);

// ── Construit sameMonthHistory + weeklyHistory depuis l'historique réel ──────
// historique : [{ date: "2026-04-07", ca_reel: 123.45 }, ...]  (dernières N semaines)
function buildHistoryFields(historique: any[]): {
  sameMonthHistory: any[];
  weeklyHistory: any[];
} {
  if (!historique || historique.length === 0) {
    return { sameMonthHistory: [], weeklyHistory: [] };
  }


  // ── weeklyHistory : S-1 = semaine la plus récente, S-2 = avant-dernière ──
// ── weeklyHistory : S-1 = semaine précédant AUJOURD'HUI, S-2 = avant-avant ──
  // On calcule la semaine calendaire précédente (lundi→dimanche)
  const today = new Date();
  const dayOfWeek = (today.getDay() + 6) % 7; // 0=lun, 6=dim
  // Début de la semaine en cours (lundi)
  const startOfCurrentWeek = new Date(today);
  startOfCurrentWeek.setDate(today.getDate() - dayOfWeek);
  startOfCurrentWeek.setHours(0, 0, 0, 0);
  // S-1 : semaine précédente complète (lundi à dimanche)
  const startOfS1 = new Date(startOfCurrentWeek);
  startOfS1.setDate(startOfS1.getDate() - 7);
  const endOfS1 = new Date(startOfCurrentWeek);
  endOfS1.setDate(endOfS1.getDate() - 1);
  // S-2 : deux semaines avant
  const startOfS2 = new Date(startOfS1);
  startOfS2.setDate(startOfS2.getDate() - 7);
  const endOfS2 = new Date(startOfS1);
  endOfS2.setDate(endOfS2.getDate() - 1);

  // Agréger le CA réel dans chaque fenêtre
  let caS1 = 0, nbS1 = 0, caS2 = 0, nbS2 = 0;
  for (const row of historique) {
    const d = new Date(row.date);
    if (d >= startOfS1 && d <= endOfS1) { caS1 += row.ca_reel ?? 0; nbS1++; }
    if (d >= startOfS2 && d <= endOfS2) { caS2 += row.ca_reel ?? 0; nbS2++; }
  }

  // Si aucune donnée dans les fenêtres calendaires réelles
  // (BD en retard) → fallback sur les 2 dernières entrées disponibles
  const sorted = [...historique].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );

 const weeklyHistory: any[] = [];
  // Pas de fallback sur ancienne donnée BD — si la semaine calendaire est vide → 0
  weeklyHistory.push({
    week: "S-1",
    caTotal: nbS1 > 0 ? caS1 : 0,
    nbJours: 7,
    isCalendaire: true,
  });
  weeklyHistory.push({
    week: "S-2",
    caTotal: nbS2 > 0 ? caS2 : 0,
    nbJours: 7,
    isCalendaire: true,
  });

  // ── sameMonthHistory : agréger le CA par mois ─────────────────────────────
  const byMonth: Record<string, { year: number; month: number; caTotal: number }> = {};
  for (const row of historique) {
    const d = new Date(row.date);
    const key = `${d.getFullYear()}-${d.getMonth() + 1}`;
    if (!byMonth[key]) {
      byMonth[key] = { year: d.getFullYear(), month: d.getMonth() + 1, caTotal: 0 };
    }
    byMonth[key].caTotal += row.ca_reel ?? 0;
  }
  const sameMonthHistory = Object.values(byMonth).sort(
    (a, b) => a.year !== b.year ? a.year - b.year : a.month - b.month,
  );

  return { sameMonthHistory, weeklyHistory };
}

// ── Mapper : réponse /prevision-horizon + historique réel → champs du tableau ─
function mapApiResponse(res: any, historique: any[] = []): ExtPrevisionFResponse {
  const caTotal: number | null = res.ca_total ?? null;
  const { sameMonthHistory, weeklyHistory } = buildHistoryFields(historique);

  return {
    ...res,
    forecastCa: caTotal,
    sameMonthHistory,
    weeklyHistory,
  } as ExtPrevisionFResponse;
}

// ── Props ─────────────────────────────────────────────────────────────────────
interface PrevisionSousFamilleViewProps {
  onSwitchToHorizon?: () => void;
  onSwitchToFamille?: () => void;
  onSwitchToFiltre?: () => void;   // ← ajouter
}

// =============================================================================
// COMPOSANT PRINCIPAL
// =============================================================================
const PrevisionSousFamilleView: React.FC<PrevisionSousFamilleViewProps> = ({
  onSwitchToHorizon = () => {},
  onSwitchToFamille = () => {},
  onSwitchToFiltre = () => {},     // ← ajouter
}) => {
  const [depots, setDepots] = useState<DepotRecord[]>([]);
  const [sousFamilles, setSousFamilles] = useState<SousFamDto[]>([]);
  const [loadingSF, setLoadingSF] = useState(false);

  // Charger TOUTES les sous-familles au montage (sans filtre famille)
  useEffect(() => {
    setLoadingSF(true);
    apiToutesSousFamilles()
      .then((data) => setSousFamilles(data))
      .finally(() => setLoadingSF(false));
  }, []);

  // ── Rôle utilisateur ──────────────────────────────────────────────────────
  const isAdmin = (() => {
    try {
      const u = session.getUser<Record<string, any>>() ?? {};
      const role: string = (u?.nom ?? u?.role ?? u?.idRole ?? "").toString().toLowerCase();
      return role.includes("admin") || role === "1";
    } catch { return false; }
  })();

  const userDepot = (() => {
    try {
      const u = session.getUser<Record<string, any>>() ?? {};
      return u?.depot ?? u?.Depot ?? u?.codDep ?? "";
    } catch { return ""; }
  })();

  const [selectedDepot, setSelectedDepot] = useState<string>("");

  useEffect(() => {
    fetchDepots().then((deps) => {
      setDepots(deps);
      if (!isAdmin && userDepot) {
        setSelectedDepot(userDepot);
      } else if (deps.length > 0) {
        const firstCode = deps[0].code_dep ?? (deps[0] as any).codDep ?? "";
        setSelectedDepot(firstCode);
      }
    });
  }, []);

  // Compatibilité avec la logique existante (activeDepot)
  const selectedDepots = new Set(selectedDepot ? [selectedDepot] : []);
  const dimension = "Sousfamille" as const;
  const [searchText, setSearchText] = useState("");

  const [horizon, setHorizon] = useState<number>(4);

  const [rowStates, setRowStates] = useState<Record<string, RowState>>({});
  const loadingAll = useRef(false);

  const [modalImage, setModalImage] = useState<{
    url: string;
    title: string;
  } | null>(null);

  

  // ── Fonctions API locales ─────────────────────────────────────────────────────
  async function apiToutesSousFamilles(): Promise<SousFamDto[]> {
    try {
      const r = await getForecastToutesSousFamilles<SousFamDto>();
      return r.data ?? [];
    } catch (e) {
      console.error("apiToutesSousFamilles error:", e);
      return [];
    }
  }

  useEffect(() => {
    setRowStates({});
  }, [horizon, selectedDepot]);

  const periodValid = true;
  const activeDepot = selectedDepots.size === 1 ? [...selectedDepots][0] : null;

  const colS2Label = "Sem S−2";
  const colS1Label = "Sem S−1";
  const colM2Label = "Mois −2";
  const colM1Label = "Mois −1";
  const colForecastLabel = `Prévision ${horizon} semaine${horizon > 1 ? "s" : ""}`;

  const items = sousFamilles.filter((item) => {
    const label = item.desgFam ?? item.codFam;
    const code = item.codFam;
    const q = searchText.toLowerCase();
    return (
      !q || label.toLowerCase().includes(q) || code.toLowerCase().includes(q)
    );
  });

  const getCode = (item: SousFamDto) => item.codFam;
  const getLabel = (item: SousFamDto) => item.desgFam ?? item.codFam;

  const loadRow = useCallback(
    async (code: string, label: string) => {
      const current = rowStates[code];
      if (current?.status === "loading") return;
      if (
        current?.status === "loaded" &&
        current.depot === activeDepot
      ) {
        setRowStates((prev) => ({ ...prev, [code]: { status: "idle" } }));
        return;
      }
      if (current?.status === "nodata") return;

      // Récupérer la famille parente depuis la liste des sous-familles
      const item = sousFamilles.find((sf) => sf.codFam === code);
      const famille = item?.codFamp ?? "";
      if (!famille) return;

      // Si aucun dépôt unique sélectionné → on ne peut pas envoyer null au modèle
      if (!activeDepot) {
        setRowStates((prev) => ({
          ...prev,
          [code]: { status: "error", error: "Veuillez sélectionner un seul dépôt" },
        }));
        return;
      }

      setRowStates((prev) => ({ ...prev, [code]: { status: "loading" } }));
      try {
        // Appel parallèle : prévision + historique réel
        // Prévision + historique réel en parallèle ; un échec de l'historique n'est pas bloquant
        const [res, histData] = await Promise.all([
          tryPrevisionHorizon({ famille, sous_famille: code, horizon, depot: activeDepot }),
          getForecastHistorique({ famille: famille, sousFamille: code, depot: activeDepot, n: 12 })
            .then((r) => (r?.data ?? []) as any[])
            .catch(() => [] as any[]),
        ]);

        if (res.success) {
          const mapped = mapApiResponse(res, histData);
          if (!mapped.forecastCa) {
            setRowStates((prev) => ({ ...prev, [code]: { status: "nodata" } }));
          } else {
            setRowStates((prev) => ({
              ...prev,
              [code]: {
                status: "loaded",
                data: mapped,
                depot: activeDepot,
                dateFrom: res.date_from,
                dateTo: res.date_to,
              },
            }));
          }
        } else {
          const errMsg = res.error ?? "Erreur modèle";
          const isNoData =
            errMsg.toLowerCase().includes("aucun") ||
            errMsg.toLowerCase().includes("no data") ||
            errMsg.toLowerCase().includes("introuvable");
          setRowStates((prev) => ({
            ...prev,
            [code]: { status: isNoData ? "nodata" : "error", error: errMsg },
          }));
        }
      } catch (e: any) {
        setRowStates((prev) => ({
          ...prev,
          [code]: { status: "error", error: e.message ?? "Erreur réseau" },
        }));
      }
   }, [
    dimension,
    activeDepot,
    items,
    sousFamilles,
    rowStates,
    horizon,
    ],
  );

  const loadAll = useCallback(async () => {
    if (loadingAll.current) return;
    loadingAll.current = true;

    const codesToLoad = items
      .map((item) => ({
        code: getCode(item),
        label: getLabel(item),
        famille: item.codFamp ?? "",
      }))
      .filter(
        ({ code }) =>
          !rowStates[code] ||
          rowStates[code].status === "idle" ||
          rowStates[code].status === "error",
      );

    if (!activeDepot) {
      loadingAll.current = false;
      setRowStates((prev) => {
        const next = { ...prev };
        codesToLoad.forEach(({ code }) => {
          next[code] = { status: "error", error: "Sélectionnez un seul dépôt" };
        });
        return next;
      });
      return;
    }

    setRowStates((prev) => {
      const next = { ...prev };
      codesToLoad.forEach(({ code }) => {
        if (!next[code] || next[code].status !== "loaded")
          next[code] = { status: "loading" };
      });
      return next;
    });

    for (const { code, famille } of codesToLoad) {
      if (!famille) continue;
      try {
        // Prévision + historique réel en parallèle ; un échec de l'historique n'est pas bloquant
        const [res, histData] = await Promise.all([
          tryPrevisionHorizon({ famille, sous_famille: code, horizon, depot: activeDepot }),
          getForecastHistorique({ famille: famille, sousFamille: code, depot: activeDepot!, n: 12 })
            .then((r) => (r?.data ?? []) as any[])
            .catch(() => [] as any[]),
        ]);

        if (res.success) {
          const mapped = mapApiResponse(res, histData);
          setRowStates((prev) => ({
            ...prev,
            [code]: mapped.forecastCa
              ? {
                  status: "loaded",
                  data: mapped,
                  depot: activeDepot,
                  dateFrom: res.date_from,
                  dateTo: res.date_to,
                }
              : { status: "nodata" },
          }));
        } else {
          const errMsg = res.error ?? "Erreur";
          const isNoData =
            errMsg.toLowerCase().includes("aucun") ||
            errMsg.toLowerCase().includes("no data") ||
            errMsg.toLowerCase().includes("introuvable");
          setRowStates((prev) => ({
            ...prev,
            [code]: { status: isNoData ? "nodata" : "error", error: errMsg },
          }));
        }
      } catch (e: any) {
        setRowStates((prev) => ({
          ...prev,
          [code]: { status: "error", error: e.message ?? "Erreur réseau" },
        }));
      }
    }
    loadingAll.current = false;
}, [
    dimension,
    activeDepot,
    items,
    rowStates,
    horizon,
  ]);

  const countLoaded = Object.values(rowStates).filter(
    (r) => r.status === "loaded",
  ).length;
  const countLoading = Object.values(rowStates).filter(
    (r) => r.status === "loading",
  ).length;
  const countError = Object.values(rowStates).filter(
    (r) => r.status === "error",
  ).length;
  const countNoData = Object.values(rowStates).filter(
    (r) => r.status === "nodata",
  ).length;

  return (
    <div
      style={{
        minHeight: "calc(100vh - 62px)",
        background: NW.bg,
        fontFamily: "Inter, system-ui, -apple-system, sans-serif",
        padding: "22px 24px",
      }}
    >
      <style>{`
        @keyframes nw-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .nw-row:hover { background: ${NW.violetLight} !important; cursor: pointer; }
        .nw-row-loaded { background: rgba(29,91,168,0.025) !important; }
        .nw-depot-label:hover { border-color: ${NW.violetMed} !important; }
        .nw-load-btn:hover { background: ${NW.violetDark} !important; }
        .nw-date-input {
          padding: 7px 11px; font-size: 13px; font-family: Inter, system-ui, -apple-system, sans-serif;
          border: 1.5px solid ${NW.violetBorder}; border-radius: 10px;
          background: #FFFFFF; color: ${NW.text}; outline: none; cursor: pointer; transition: border-color 0.15s;
        }
        .nw-date-input:focus { border-color: ${NW.violet}; box-shadow: 0 0 0 3px rgba(29,91,168,0.12); }
      `}</style>

      {modalImage && (
        <ImageModal
          imageUrl={modalImage.url}
          title={modalImage.title}
          onClose={() => setModalImage(null)}
        />
      )}

      {/* ── En-tête ───────────────────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          marginBottom: 18,
        }}
      >
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 14,
            background: "linear-gradient(135deg,#184B8C,#1D5BA8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 22,
            boxShadow: "0 4px 16px rgba(29,91,168,0.35)",
            flexShrink: 0,
          }}
        >
          🔮
        </div>
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <h1
            style={{
              margin: 0,
              fontFamily: "Poppins, Inter, system-ui, sans-serif",
              fontWeight: 800,
              fontSize: 22,
              color: NW.text,
              letterSpacing: "-0.4px",
            }}
          >
           Prévision par Sous-famille
          </h1>

          {/* ── Boutons navigation ──────────────────────────────────────── */}
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={onSwitchToFamille}       // ← changer onSwitchToHorizon → onSwitchToFamille
              style={{
                padding: "8px 16px",
                borderRadius: 10,
                border: `1.5px solid ${NW.violetBorder}`,
                background: "#FFFFFF",
                color: NW.violet,
                fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                fontWeight: 700,
                fontSize: 13,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              📦 Famille
            </button>
            <button
              style={{
                padding: "8px 16px",
                borderRadius: 10,
                border: "none",
                background: NW.violet,
                color: "#FFFFFF",
                fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                fontWeight: 700,
                fontSize: 13,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              🗂 Sous-famille
            </button>
            <button
              onClick={onSwitchToFiltre}       // ← ajouter
              style={{
                padding: "8px 16px",
                borderRadius: 10,
                border: `1.5px solid ${NW.violetBorder}`, background: "#FFFFFF", color: NW.violet ,
                fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                fontWeight: 700,
                fontSize: 13,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              🔍 Filtre
            </button>
          </div>
        </div>

        {/* Compteurs */}
        {countLoaded + countLoading + countError + countNoData > 0 && (
          <div
            style={{ display: "flex", gap: 8, fontSize: 11, fontWeight: 700 }}
          >
            {countLoaded > 0 && (
              <span
                style={{
                  background: NW.greenBg,
                  color: NW.green,
                  padding: "4px 10px",
                  borderRadius: 99,
                }}
              >
                ✓ {countLoaded} chargée{countLoaded > 1 ? "s" : ""}
              </span>
            )}
            {countLoading > 0 && (
              <span
                style={{
                  background: NW.violetLight,
                  color: NW.violet,
                  padding: "4px 10px",
                  borderRadius: 99,
                }}
              >
                ⏳ {countLoading}
              </span>
            )}
            {countNoData > 0 && (
              <span
                style={{
                  background: NW.redBg,
                  color: NW.red,
                  padding: "4px 10px",
                  borderRadius: 99,
                }}
              >
                ∅ {countNoData} sans données
              </span>
            )}
            {countError > 0 && (
              <span
                style={{
                  background: NW.redBg,
                  color: NW.red,
                  padding: "4px 10px",
                  borderRadius: 99,
                }}
              >
                ✗ {countError} erreur{countError > 1 ? "s" : ""}
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── Barre de contrôle ─────────────────────────────────────────────── */}
      <div
        style={{
          background: NW.card,
          borderRadius: 18,
          border: `1.5px solid ${NW.violetBorder}`,
          boxShadow: NW.shadow,
          padding: "16px 20px",
          marginBottom: 16,
          display: "flex",
          flexWrap: "wrap",
          gap: 20,
          alignItems: "flex-start",
        }}
      >
        {/* Dépôts */}
        <div style={{ flex: 2, minWidth: 260 }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 800,
              color: NW.textMuted,
              textTransform: "uppercase",
              letterSpacing: 1.2,
              marginBottom: 8,
            }}
          >
            🏭 Dépôts
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
            {depots.slice(0, 18).map((d, idx) => {
              const depCode = d.code_dep ?? (d as any).codDep ?? String(idx);
              const isChecked = selectedDepots.has(depCode);
              return (
                <label
                  key={`depot-${depCode}-${idx}`}
                  className="nw-depot-label"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    cursor: "pointer",
                    padding: "5px 11px",
                    borderRadius: 8,
                    border: `1.5px solid ${isChecked ? NW.violet : NW.border}`,
                    background: isChecked ? NW.violetLight : "transparent",
                    fontSize: 12,
                    fontWeight: 600,
                    color: isChecked ? NW.violet : NW.text,
                    userSelect: "none",
                    transition: "all 0.15s",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() =>
                      setSelectedDepots((prev) => {
                        const next = new Set(prev);
                        if (next.has(depCode)) next.delete(depCode);
                        else next.add(depCode);
                        return next;
                      })
                    }
                    style={{ accentColor: NW.violet, width: 13, height: 13 }}
                  />
                  {depCode}
                </label>
              );
            })}
            
            {depots.length === 0 && (
              <span style={{ fontSize: 12, color: NW.textMuted, fontStyle: "italic" }}>
                Chargement des dépôts…
              </span>
            )}
            {selectedDepots.size > 1 && (
              <span
                style={{
                  fontSize: 10,
                  color: NW.orange,
                  padding: "5px 9px",
                  borderRadius: 8,
                  background: NW.orangeBg,
                  border: `1px solid ${NW.orange}33`,
                  fontWeight: 700,
                }}
              >
                ⚠ Multi-dépôt → modèle global utilisé
              </span>
            )}
          </div>
        </div>

        <div
          style={{
            width: 1,
            background: NW.border,
            alignSelf: "stretch",
            flexShrink: 0,
          }}
        />

        
         {/* Horizon */}
        <div style={{ flexShrink: 0 }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 800,
              color: NW.textMuted,
              textTransform: "uppercase",
              letterSpacing: 1.2,
              marginBottom: 8,
            }}
          >
            📅 Horizon (semaines)
          </div>
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
            {[1,2,3,4,5,6,7,8,9,10,11].map((h) => (
              <button
                key={h}
                onClick={() => setHorizon(h)}
                style={{
                  width: 44,
                  height: 36,
                  borderRadius: 10,
                  border: `1.5px solid ${horizon === h ? NW.violet : NW.violetBorder}`,
                  background: horizon === h ? NW.violet : "transparent",
                  color: horizon === h ? "#FFFFFF" : NW.text,
                  fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
              >
                {h}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 10, color: NW.textMuted, fontWeight: 600, marginTop: 6 }}>
            Horizon sélectionné :{" "}
            <strong style={{ color: NW.violet }}>{horizon} semaine{horizon > 1 ? "s" : ""}</strong>
          </div>
        </div>
      </div>

      {/* ── Barre outils tableau ──────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 10,
          flexWrap: "wrap",
        }}
      >
        <input
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Filtrer…"
          style={{
            padding: "7px 13px",
            fontSize: 12,
            border: `1.5px solid ${NW.border}`,
            borderRadius: 10,
            fontFamily: "Inter, system-ui, -apple-system, sans-serif",
            color: NW.text,
            background: NW.card,
            outline: "none",
            width: 160,
          }}
        />
        <span style={{ fontSize: 12, color: NW.textMuted, fontWeight: 600 }}>
          {loadingSF ? <Spinner /> : `${items.length} sous-famille(s)`}
        </span>
        {periodValid && (
          <button
            className="nw-load-btn"
            onClick={loadAll}
            disabled={countLoading > 0}
            style={{
              marginLeft: "auto",
              padding: "7px 16px",
              fontSize: 12,
              fontWeight: 700,
              border: "none",
              borderRadius: 10,
              background:
                countLoading > 0
                  ? "#B0CBEB"
                  : "linear-gradient(135deg,#184B8C,#1D5BA8)",
              color: "#FFFFFF",
              cursor: countLoading > 0 ? "not-allowed" : "pointer",
              fontFamily: "Inter, system-ui, -apple-system, sans-serif",
              display: "flex",
              alignItems: "center",
              gap: 6,
              boxShadow:
                countLoading > 0 ? "none" : "0 2px 12px rgba(29,91,168,0.3)",
              transition: "all 0.15s",
            }}
          >
            {countLoading > 0 ? (
              <>
                <Spinner /> Chargement…
              </>
            ) : (
              "⚡ Charger tout"
            )}
          </button>
        )}
        {countLoaded + countError + countNoData > 0 && (
          <button
            onClick={() => setRowStates({})}
            style={{
              padding: "7px 14px",
              fontSize: 12,
              fontWeight: 600,
              border: `1.5px solid ${NW.border}`,
              borderRadius: 10,
              background: "transparent",
              color: NW.textMuted,
              cursor: "pointer",
              fontFamily: "Inter, system-ui, -apple-system, sans-serif",
            }}
          >
            🔄 Réinitialiser
          </button>
        )}
      </div>

      {/* ── Tableau principal ─────────────────────────────────────────────── */}
      <div
        style={{
          background: NW.card,
          borderRadius: 18,
          border: `1.5px solid ${NW.violetBorder}`,
          boxShadow: NW.shadowStrong,
          overflow: "hidden",
        }}
      >
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontFamily: "Inter, system-ui, -apple-system, sans-serif",
              fontSize: 13,
            }}
          >
            <thead>
              <tr style={{ background: NW.violetLight }}>
                <th
                  style={{
                    padding: "12px 16px",
                    textAlign: "left",
                    fontWeight: 800,
                    fontSize: 11,
                    color: NW.violet,
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    borderBottom: `2px solid ${NW.violetBorder}`,
                    minWidth: 200,
                    position: "sticky",
                    left: 0,
                    background: NW.violetLight,
                    zIndex: 2,
                  }}
                >
                 🗂 Sous-famille
                </th>
                <th
                  style={{
                    padding: "12px 16px",
                    textAlign: "right",
                    fontWeight: 800,
                    fontSize: 11,
                    color: NW.textMuted,
                    textTransform: "uppercase",
                    letterSpacing: 0.8,
                    borderBottom: `2px solid ${NW.violetBorder}`,
                    minWidth: 130,
                    whiteSpace: "nowrap",
                  }}
                >
                  {colM2Label}
                </th>
                <th
                  style={{
                    padding: "12px 16px",
                    textAlign: "right",
                    fontWeight: 800,
                    fontSize: 11,
                    color: NW.textMuted,
                    textTransform: "uppercase",
                    letterSpacing: 0.8,
                    borderBottom: `2px solid ${NW.violetBorder}`,
                    minWidth: 130,
                    whiteSpace: "nowrap",
                  }}
                >
                  {colM1Label}
                </th>
                <th
                  style={{
                    padding: "12px 16px",
                    textAlign: "right",
                    fontWeight: 800,
                    fontSize: 11,
                    color: NW.blue,
                    textTransform: "uppercase",
                    letterSpacing: 0.8,
                    borderBottom: `2px solid ${NW.violetBorder}`,
                    minWidth: 150,
                    whiteSpace: "nowrap",
                  }}
                >
                  {colS2Label}
                </th>
                <th
                  style={{
                    padding: "12px 16px",
                    textAlign: "right",
                    fontWeight: 800,
                    fontSize: 11,
                    color: NW.blue,
                    textTransform: "uppercase",
                    letterSpacing: 0.8,
                    borderBottom: `2px solid ${NW.violetBorder}`,
                    minWidth: 150,
                    whiteSpace: "nowrap",
                  }}
                >
                  {colS1Label}
                </th>
                <th
                  style={{
                    padding: "12px 16px",
                    textAlign: "right",
                    fontWeight: 800,
                    fontSize: 11,
                    color: NW.violet,
                    textTransform: "uppercase",
                    letterSpacing: 0.8,
                    borderBottom: `2px solid ${NW.violetBorder}`,
                    minWidth: 180,
                    whiteSpace: "nowrap",
                    background: "rgba(29,91,168,0.06)",
                    borderLeft: `3px solid ${NW.violet}22`,
                  }}
                >
                  🔮 {colForecastLabel}
                </th>
              </tr>
              <tr style={{ background: "rgba(29,91,168,0.015)" }}>
                <td
                  style={{
                    padding: "4px 16px",
                    fontSize: 10,
                    color: NW.textMuted,
                    borderBottom: `1px solid ${NW.border}`,
                    position: "sticky",
                    left: 0,
                    background: "rgba(238,244,251,0.3)",
                  }}
                >
                  Cliquer sur une ligne pour charger
                </td>
                {[
                  "CA mois",
                  "CA mois",
                  "Sem. réelle",
                  "Sem. réelle",
                  "Prévision",
                ].map((s, i) => (
                  <td
                    key={i}
                    style={{
                      padding: "4px 16px",
                      fontSize: 10,
                      color: i === 4 ? NW.violet : NW.textMuted,
                      textAlign: "right",
                      borderBottom: `1px solid ${NW.border}`,
                      fontStyle: "italic",
                      background:
                        i === 4 ? "rgba(29,91,168,0.02)" : "transparent",
                      fontWeight: i === 4 ? 700 : 400,
                    }}
                  >
                    {s}
                  </td>
                ))}
              </tr>
            </thead>
            <tbody>
              {loadingSF ? (
                <tr>
                  <td
                    colSpan={6}
                    style={{
                      padding: 48,
                      textAlign: "center",
                      color: NW.textMuted,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 10,
                      }}
                    >
                      <Spinner />
                     <span style={{ fontSize: 13, fontWeight: 600 }}>
                        Chargement des sous-familles…
                      </span>
                    </div>
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    style={{
                      padding: 48,
                      textAlign: "center",
                      color: NW.textMuted,
                      fontSize: 13,
                    }}
                  >
                    {searchText
                      ? `Aucun résultat pour « ${searchText} »`
                      : "Aucune donnée disponible"}
                  </td>
                </tr>
              ) : (
                items.map((item, idx) => {
                  const code = getCode(item);
                  const label = getLabel(item);
                  const rs = rowStates[code];
                  const status: RowStatus = rs?.status ?? "idle";
                  const isLoaded = status === "loaded";
                  const isLoading = status === "loading";
                  const isError = status === "error";
                  const isNoData = status === "nodata";
                  const isActive = isLoaded || isLoading || isError || isNoData;

                  const hist = rs?.data?.sameMonthHistory ?? [];
                  const weekly = rs?.data?.weeklyHistory ?? [];
                  const fy = rs?.forecastYear;

                  const today = new Date().toISOString().slice(0, 10);
                  const m1range = getMonthBounds(today, 1);
                  const m2range = getMonthBounds(today, 2);

                  const m2data =
                    m2range && fy
                      ? (hist as any[]).find((h) => {
                          const d = new Date(m2range.from);
                          return (
                            h.year === d.getFullYear() &&
                            h.month === d.getMonth() + 1
                          );
                        })
                      : null;
                  const m1data =
                    m1range && fy
                      ? (hist as any[]).find((h) => {
                          const d = new Date(m1range.from);
                          return (
                            h.year === d.getFullYear() &&
                            h.month === d.getMonth() + 1
                          );
                        })
                      : null;

                  const m2ca =
                    m2data?.caTotal ??
                    (fy
                      ? (hist as any[]).find((h) => h.year === fy - 2)?.caTotal
                      : null) ??
                    null;
                  const m1ca =
                    m1data?.caTotal ??
                    (fy
                      ? (hist as any[]).find((h) => h.year === fy - 1)?.caTotal
                      : null) ??
                    null;

                  const s2 =
                    (weekly as any[]).find((w) => w.week === "S-2") ?? null;
                  const s1 =
                    (weekly as any[]).find((w) => w.week === "S-1") ?? null;
                  const forecast = rs?.data?.forecastCa ?? null;

                  const yoyPct =
                    forecast != null && m1ca != null && m1ca > 0
                      ? ((forecast - m1ca) / m1ca) * 100
                      : null;

                  return (
                    <tr
                      key={`${code}-${idx}`}
                      className={`nw-row${isLoaded ? " nw-row-loaded" : ""}`}
                      onClick={() => loadRow(code, label)}
                      style={{
                        borderBottom: `1px solid ${NW.border}`,
                        transition: "background 0.12s",
                        height: 54,
                      }}
                    >
                      <td
                        style={{
                          padding: "0 16px",
                          position: "sticky",
                          left: 0,
                          background: isLoaded
                            ? "rgba(29,91,168,0.025)"
                            : NW.card,
                          zIndex: 1,
                          borderRight: `1px solid ${NW.border}`,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                          }}
                        >
                          <div
                            style={{
                              width: 16,
                              height: 16,
                              borderRadius: 5,
                              border: `2px solid ${isActive ? NW.violet : NW.border}`,
                              background: isActive
                                ? isNoData
                                  ? NW.red
                                  : NW.violet
                                : "transparent",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                              transition: "all 0.12s",
                            }}
                          >
                            {isLoading ? (
                              <Spinner />
                            ) : isLoaded ? (
                              <span
                                style={{
                                  color: "#FFFFFF",
                                  fontSize: 9,
                                  lineHeight: 1,
                                  fontWeight: 900,
                                }}
                              >
                                ✓
                              </span>
                            ) : isError ? (
                              <span
                                style={{
                                  color: "#FFFFFF",
                                  fontSize: 9,
                                  lineHeight: 1,
                                }}
                              >
                                ✕
                              </span>
                            ) : isNoData ? (
                              <span
                                style={{
                                  color: "#FFFFFF",
                                  fontSize: 9,
                                  lineHeight: 1,
                                }}
                              >
                                ∅
                              </span>
                            ) : null}
                          </div>
                          <div>
                            <div
                              style={{
                                fontWeight: isActive ? 800 : 600,
                                color: isNoData
                                  ? NW.red
                                  : isActive
                                    ? NW.violet
                                    : NW.text,
                                fontSize: 13,
                                lineHeight: 1.2,
                              }}
                            >
                              {label}
                            </div>
                            <div
                              style={{
                                fontSize: 10,
                                color: NW.textMuted,
                                fontWeight: 600,
                              }}
                            >
                              {code}
                            </div>
                          </div>
                        </div>
                      </td>

                      {isLoading ? (
                        <td
                          colSpan={5}
                          style={{
                            padding: "0 16px",
                            textAlign: "center",
                            color: NW.textMuted,
                            fontSize: 12,
                            fontWeight: 600,
                          }}
                        >
                          <div
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <Spinner /> Calcul de la prévision…
                          </div>
                        </td>
                      ) : isNoData ? (
                        <td
                          colSpan={5}
                          style={{
                            padding: "0 16px",
                            color: NW.red,
                            fontSize: 12,
                            fontWeight: 700,
                            textAlign: "center",
                          }}
                        >
                          ⚠ Aucune donnée trouvée
                        </td>
                      ) : isError ? (
                        <td
                          colSpan={5}
                          style={{
                            padding: "0 16px",
                            color: NW.red,
                            fontSize: 12,
                            fontWeight: 600,
                          }}
                        >
                          ❌{" "}
                          {rs.error?.includes("Aucun modèle")
                            ? "Modèle non entraîné pour ce label"
                            : rs.error}
                        </td>
                      ) : isLoaded ? (
                        <React.Fragment key={`${code}-loaded`}>
                          <DataCell value={m2ca} />
                          <DataCell value={m1ca} compareWith={m2ca} />
                          <DataCell
                            value={s2?.caTotal ?? null}
                            muted
                            incomplete={s2 != null && s2.nbJours < 7}
                          />
                          <DataCell
                            value={s1?.caTotal ?? null}
                            muted
                            compareWith={s2?.caTotal ?? null}
                          />
                          <td
                            style={{
                              padding: "0 16px",
                              textAlign: "right",
                              verticalAlign: "middle",
                              background: "rgba(29,91,168,0.06)",
                              borderLeft: `3px solid ${NW.violet}22`,
                            }}
                          >
                            <div
                              style={{
                                fontWeight: 900,
                                fontSize: 14,
                                color: NW.violet,
                                lineHeight: 1.2,
                              }}
                            >
                              {fmtCa(forecast)}
                            </div>
                            {yoyPct != null && (
                              <div
                                style={{
                                  fontSize: 10,
                                  fontWeight: 700,
                                  color:
                                    yoyPct > 0
                                      ? NW.green
                                      : yoyPct < 0
                                        ? NW.red
                                        : NW.textMuted,
                                  marginTop: 2,
                                }}
                              >
                                {fmtPct(yoyPct)} vs M−1
                              </div>
                            )}
                          </td>
                        </React.Fragment>
                      ) : (
                        <React.Fragment key={`${code}-idle`}>
                          {[0, 1, 2, 3].map((i) => (
                            <td
                              key={i}
                              style={{
                                padding: "0 16px",
                                textAlign: "right",
                                color: NW.border,
                                fontSize: 12,
                              }}
                            >
                              {periodValid ? "—" : ""}
                            </td>
                          ))}
                          <td
                            style={{
                              padding: "0 16px",
                              textAlign: "right",
                              background: "rgba(29,91,168,0.03)",
                              borderLeft: `3px solid ${NW.violet}11`,
                            }}
                          >
                            {periodValid ? (
                              <span
                                style={{
                                  fontSize: 11,
                                  color: NW.violetMed,
                                  fontWeight: 600,
                                  fontStyle: "italic",
                                }}
                              >
                                ← Cliquer pour charger
                              </span>
                            ) : (
                              <span style={{ fontSize: 11, color: NW.border }}>
                                Choisir une période ↑
                              </span>
                            )}
                          </td>
                        </React.Fragment>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>

            {countLoaded > 1 &&
              (() => {
                const loadedRows = Object.values(rowStates).filter(
                  (r) => r.status === "loaded" && r.data,
                );
                const totalForecast = loadedRows.reduce(
                  (s, r) => s + (r.data?.forecastCa ?? 0),
                  0,
                );
                const totalM1 = loadedRows.reduce((s, r) => {
                  const fy = r.forecastYear;
                  const hist = r.data?.sameMonthHistory ?? [];
                  const m1entry = fy
                    ? (hist as any[]).find((h) => h.year === fy - 1)
                    : null;
                  return s + (m1entry?.caTotal ?? 0);
                }, 0);
                const totalM2 = loadedRows.reduce((s, r) => {
                  const fy = r.forecastYear;
                  const hist = r.data?.sameMonthHistory ?? [];
                  const m2entry = fy
                    ? (hist as any[]).find((h) => h.year === fy - 2)
                    : null;
                  return s + (m2entry?.caTotal ?? 0);
                }, 0);
                const totalS1 = loadedRows.reduce(
                  (s, r) =>
                    s +
                    ((r.data?.weeklyHistory as any[])?.find(
                      (w) => w.week === "S-1",
                    )?.caTotal ?? 0),
                  0,
                );
                const totalS2 = loadedRows.reduce(
                  (s, r) =>
                    s +
                    ((r.data?.weeklyHistory as any[])?.find(
                      (w) => w.week === "S-2",
                    )?.caTotal ?? 0),
                  0,
                );
                return (
                  <tfoot>
                    <tr
                      style={{
                        background: NW.violetLight,
                        borderTop: `2px solid ${NW.violetBorder}`,
                      }}
                    >
                      <td
                        style={{
                          padding: "10px 16px",
                          fontWeight: 800,
                          fontSize: 12,
                          color: NW.violet,
                          position: "sticky",
                          left: 0,
                          background: NW.violetLight,
                        }}
                      >
                        TOTAL ({countLoaded} sous-familles chargées)
                      </td>
                      <td
                        style={{
                          padding: "10px 16px",
                          textAlign: "right",
                          fontWeight: 800,
                          fontSize: 13,
                          color: NW.text,
                        }}
                      >
                        {fmtCa(totalM2)}
                      </td>
                      <td
                        style={{
                          padding: "10px 16px",
                          textAlign: "right",
                          fontWeight: 800,
                          fontSize: 13,
                          color: NW.text,
                        }}
                      >
                        {fmtCa(totalM1)}
                      </td>
                      <td
                        style={{
                          padding: "10px 16px",
                          textAlign: "right",
                          fontWeight: 700,
                          fontSize: 12,
                          color: NW.textMuted,
                        }}
                      >
                        {fmtCa(totalS2)}
                      </td>
                      <td
                        style={{
                          padding: "10px 16px",
                          textAlign: "right",
                          fontWeight: 700,
                          fontSize: 12,
                          color: NW.textMuted,
                        }}
                      >
                        {fmtCa(totalS1)}
                      </td>
                      <td
                        style={{
                          padding: "10px 16px",
                          textAlign: "right",
                          fontWeight: 900,
                          fontSize: 15,
                          color: NW.violet,
                          background: "rgba(29,91,168,0.08)",
                          borderLeft: `3px solid ${NW.violet}33`,
                        }}
                      >
                        {fmtCa(totalForecast)}
                      </td>
                    </tr>
                  </tfoot>
                );
              })()}
          </table>
        </div>
      </div>

      {/* ── Légende ───────────────────────────────────────────────────────── */}
      <div
        style={{
          marginTop: 14,
          display: "flex",
          gap: 20,
          fontSize: 11,
          color: NW.textMuted,
          flexWrap: "wrap",
        }}
      >
        <span>
          📅 <strong>Sem. S-1</strong> = semaine complète (lun–dim) précédant la
          date de début
        </span>
        <span>
          📅 <strong>Sem. S-2</strong> = avant-dernière semaine complète
        </span>
        <span>
          📅 <strong>Mois −1 / −2</strong> = mois calendaires précédant la
          période choisie
        </span>
        <span>🔵 Les % = variation vs la colonne précédente</span>
      </div>
    </div>
  );
};

export default PrevisionSousFamilleView;